import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseUnits } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import { toBigDecimal } from '@vertex-protocol/client';
import { removeDecimals } from '@vertex-protocol/utils';
import { useSubaccountFeeRates } from 'client/hooks/query/subaccount/useSubaccountFeeRates';
import { useAccount, useSwitchChain, useWalletClient } from 'wagmi';
import {
  DEPOSIT_CHAIN_BY_ID,
  INK_DEPOSIT_CHAIN,
} from '../../nadoDeposit/config/chains';
import {
  NadoWithdrawBridgeProgressData,
  NadoWithdrawButtonState,
  NadoWithdrawFormActions,
  NadoWithdrawFormData,
} from '../types';
import { useExecuteWithdrawCollateral } from 'client/hooks/execute/useExecuteWithdrawCollateral';
import { useMaxWithdrawableAmount } from 'client/hooks/query/subaccount/useMaxWithdrawableAmount';
import { useSourceTokenBalance } from '../../nadoDeposit/hooks/useSourceTokenBalance';
import { useAcrossQuote, useAcrossExecute } from '../../nadoDeposit/hooks/useAcrossBridge';
import { BridgeMode } from '../../nadoDeposit/types';
import { getNadoWithdrawRoute } from '../utils/withdrawRoutes';
import {
  buildNadoWithdrawAcrossQuoteParams,
  canCreateLockedWithdrawQuote,
  getExpectedNadoWithdrawBridgeInputAmount,
  getNadoWithdrawDestinationArrivalState,
  getNadoWithdrawInkArrivalState,
  getNadoWithdrawWalletState,
  shouldCaptureNadoWithdrawDestinationBaseline,
} from '../utils/withdrawSessionState';
import { clientEnv } from 'common/environment/clientEnv';
import { useIsPrivyEmbeddedWallet } from 'client/hooks/util/useIsPrivyEmbeddedWallet';
import {
  canStartNadoFundingAutomationStep,
  getNadoFundingAutomationStatus,
  shouldSubmitNadoFundingAutomationStep,
  shouldStopNadoFundingAutomation,
} from 'client/modules/collateral/nadoFundingAutomation/nadoFundingAutomation';

interface UseNadoWithdrawFormParams {
  selectedChainId: number;
  selectedTokenSymbol: string;
}

interface UseNadoWithdrawFormReturn {
  formData: NadoWithdrawFormData;
  formActions: NadoWithdrawFormActions;
  bridgeProgress: NadoWithdrawBridgeProgressData | undefined;
}

const RAW_TOKEN_DECIMALS = 1_000_000;

function formatTokenAmount(raw: bigint | undefined, decimals = 4) {
  if (raw === undefined) return '0.00';
  return (Number(raw) / RAW_TOKEN_DECIMALS).toFixed(decimals);
}

function toRawFee(withdrawalFee: ReturnType<typeof removeDecimals> | undefined) {
  if (!withdrawalFee) return undefined;
  return BigInt(withdrawalFee.multipliedBy(RAW_TOKEN_DECIMALS).integerValue().toString());
}

/**
 * Master orchestration hook for the Nado withdrawal flow.
 *
 * v1 reverse funding supports Ethereum destination routes through Across:
 *   - Nado USDC/product 5 → Ink USDC → Ethereum USDC
 *   - Nado USDT0/product 0 → Ink USDT0 → Ethereum USDT
 */
export function useNadoWithdrawForm({
  selectedChainId,
  selectedTokenSymbol,
}: UseNadoWithdrawFormParams): UseNadoWithdrawFormReturn {
  const { address, chainId: connectedChainId } = useAccount();
  const { switchChainAsync, isPending: isSwitchingChain } = useSwitchChain();
  const { data: inkWalletClient } = useWalletClient({ chainId: INK_DEPOSIT_CHAIN.chainId });
  const queryClient = useQueryClient();
  const isPrivyEmbeddedWallet = useIsPrivyEmbeddedWallet();
  const automationSubmitInFlightRef = useRef(false);
  const lastAutomationSubmittedStateRef = useRef<NadoWithdrawButtonState | undefined>();
  const formSessionVersionRef = useRef(0);

  const [amount, setAmount] = useState('');
  const [bridgeTxHash, setBridgeTxHash] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [errorDetails, setErrorDetails] = useState<string | undefined>();
  const [withdrawSubmitted, setWithdrawSubmitted] = useState(false);
  const [withdrawComplete, setWithdrawComplete] = useState(false);
  const [preWithdrawBalance, setPreWithdrawBalance] = useState<bigint | undefined>();
  const [preDestinationBalance, setPreDestinationBalance] = useState<bigint | undefined>();
  const [lockedAddress, setLockedAddress] = useState<`0x${string}` | undefined>();
  const [bridgeSubmittedAtMs, setBridgeSubmittedAtMs] = useState<number | undefined>();
  const [arrivalNowMs, setArrivalNowMs] = useState(() => Date.now());
  const [expectedDestinationOutputAmount, setExpectedDestinationOutputAmount] = useState<bigint | undefined>();
  const [bridgeMode, setBridgeMode] = useState<BridgeMode>('fast');
  const [isFundingAutomationActive, setIsFundingAutomationActive] = useState(false);
  const fillRefreshRequestedRef = useRef(false);

  const destChainConfig = DEPOSIT_CHAIN_BY_ID[selectedChainId];
  const route = useMemo(
    () => getNadoWithdrawRoute({ selectedChainId, selectedTokenSymbol }),
    [selectedChainId, selectedTokenSymbol],
  );
  const destinationTokenSymbol = route?.destinationSymbol ?? selectedTokenSymbol;
  const productId = route?.nadoProductId ?? 0;
  const isOnInk = connectedChainId === INK_DEPOSIT_CHAIN.chainId;
  const hasStartedTransaction = withdrawSubmitted || !!bridgeTxHash;

  const { data: maxWithdrawable } = useMaxWithdrawableAmount({
    productId,
    spotLeverage: false,
  });

  const { data: feeRates } = useSubaccountFeeRates();
  const withdrawalFee = useMemo(() => {
    if (!route || !feeRates?.withdrawal) return undefined;
    const rawFee = feeRates.withdrawal[route.nadoProductId];
    if (rawFee === undefined) return undefined;
    return removeDecimals(toBigDecimal(rawFee));
  }, [feeRates, route]);
  const feeRaw = useMemo(() => toRawFee(withdrawalFee), [withdrawalFee]);

  const parsedAmount = useMemo(() => {
    if (!amount) return undefined;
    try {
      return parseUnits(amount, 6);
    } catch {
      return undefined;
    }
  }, [amount]);

  const expectedBridgeInputAmount = useMemo(
    () => getExpectedNadoWithdrawBridgeInputAmount({ parsedAmount, feeRaw }),
    [parsedAmount, feeRaw],
  );

  const canLockQuote = canCreateLockedWithdrawQuote({
    route,
    parsedAmount,
    feeRaw,
    address,
  });

  const walletState = getNadoWithdrawWalletState({
    lockedAddress,
    currentAddress: address,
    hasStartedTransaction,
  });
  const walletClientAddress = inkWalletClient?.account?.address;
  const walletClientMismatch =
    !!lockedAddress &&
    !!walletClientAddress &&
    lockedAddress.toLowerCase() !== walletClientAddress.toLowerCase();
  const hasWalletMismatch = walletState === 'mismatch' || walletClientMismatch;

  useEffect(() => {
    if (walletState !== 'reset') return;
    setLockedAddress(undefined);
    setPreWithdrawBalance(undefined);
    setPreDestinationBalance(undefined);
    setBridgeTxHash(undefined);
    setBridgeSubmittedAtMs(undefined);
    setExpectedDestinationOutputAmount(undefined);
    setWithdrawSubmitted(false);
    setWithdrawComplete(false);
  }, [walletState]);

  useEffect(() => {
    if (!canLockQuote || hasStartedTransaction || !address) return;
    if (lockedAddress?.toLowerCase() === address.toLowerCase()) return;
    setLockedAddress(address);
    setPreWithdrawBalance(undefined);
    setPreDestinationBalance(undefined);
    setBridgeTxHash(undefined);
    setBridgeSubmittedAtMs(undefined);
    setExpectedDestinationOutputAmount(undefined);
    setWithdrawSubmitted(false);
    setWithdrawComplete(false);
  }, [address, canLockQuote, hasStartedTransaction, lockedAddress]);

  const { data: inkWalletBalance } = useSourceTokenBalance(
    route ? INK_DEPOSIT_CHAIN.chainId : undefined,
    lockedAddress,
    route?.inkInputToken,
  );
  const { data: destinationWalletBalance } = useSourceTokenBalance(
    route?.destinationChainId,
    lockedAddress,
    route?.destinationOutputToken,
  );

  const acrossQuoteParams = buildNadoWithdrawAcrossQuoteParams({
    route,
    lockedAddress,
    expectedBridgeInputAmount,
  });
  const {
    data: acrossQuoteData,
    isLoading: isAcrossQuoteLoading,
    isError: isAcrossQuoteError,
    error: acrossQuoteError,
  } = useAcrossQuote(acrossQuoteParams);
  const acrossExecute = useAcrossExecute(route ? INK_DEPOSIT_CHAIN.chainId : undefined);

  const nadoWithdraw = useExecuteWithdrawCollateral();

  const inkArrivalState = getNadoWithdrawInkArrivalState({
    preWithdrawBalance,
    currentInkBalance: inkWalletBalance?.raw,
    expectedBridgeInputAmount,
  });
  const destinationArrivalState = getNadoWithdrawDestinationArrivalState({
    preDestinationBalance,
    currentDestinationBalance: destinationWalletBalance?.raw,
    expectedOutputAmount: expectedDestinationOutputAmount ?? acrossQuoteData?.outputAmount,
    submittedAtMs: bridgeSubmittedAtMs,
    nowMs: arrivalNowMs,
  });

  useEffect(() => {
    if (!withdrawSubmitted || withdrawComplete) return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: [
          'sourceTokenBalance',
          INK_DEPOSIT_CHAIN.chainId,
          lockedAddress,
          route?.inkInputToken,
        ],
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [lockedAddress, queryClient, route?.inkInputToken, withdrawComplete, withdrawSubmitted]);

  useEffect(() => {
    if (withdrawSubmitted && !withdrawComplete && inkArrivalState.status === 'ready') {
      setWithdrawComplete(true);
    }
  }, [inkArrivalState.status, withdrawComplete, withdrawSubmitted]);

  useEffect(() => {
    if (!shouldCaptureNadoWithdrawDestinationBaseline({
      withdrawComplete,
      preDestinationBalance,
      currentDestinationBalance: destinationWalletBalance?.raw,
    })) {
      return;
    }
    setPreDestinationBalance(destinationWalletBalance?.raw);
  }, [destinationWalletBalance?.raw, preDestinationBalance, withdrawComplete]);

  useEffect(() => {
    if (
      !bridgeTxHash ||
      destinationArrivalState.status === 'delivered' ||
      destinationArrivalState.status === 'partial_arrival' ||
      destinationArrivalState.status === 'arrival_timeout'
    ) return;
    const interval = setInterval(() => {
      setArrivalNowMs(Date.now());
      queryClient.invalidateQueries({
        queryKey: [
          'sourceTokenBalance',
          route?.destinationChainId,
          lockedAddress,
          route?.destinationOutputToken,
        ],
      });
    }, fillRefreshRequestedRef.current ? 1500 : 3000);
    return () => clearInterval(interval);
  }, [
    bridgeTxHash,
    destinationArrivalState.status,
    lockedAddress,
    queryClient,
    route?.destinationChainId,
    route?.destinationOutputToken,
  ]);

  const decimalAdjustedMaxWithdrawable = useMemo(() => {
    if (!maxWithdrawable || maxWithdrawable.isZero()) return undefined;
    const base = removeDecimals(maxWithdrawable);
    if (withdrawalFee) {
      return base.plus(withdrawalFee);
    }
    return base;
  }, [maxWithdrawable, withdrawalFee]);

  const availableBalanceFormatted = useMemo(() => {
    if (!decimalAdjustedMaxWithdrawable) return '0.00';
    try {
      return decimalAdjustedMaxWithdrawable.toFixed(6);
    } catch {
      return '0.00';
    }
  }, [decimalAdjustedMaxWithdrawable]);

  const hasEnoughBalance = useMemo(() => {
    if (!parsedAmount || parsedAmount === BigInt("0")) return false;
    if (!decimalAdjustedMaxWithdrawable) return false;
    try {
      const maxRaw = BigInt(
        decimalAdjustedMaxWithdrawable
          .multipliedBy(RAW_TOKEN_DECIMALS)
          .integerValue()
          .toString(),
      );
      return parsedAmount <= maxRaw;
    } catch {
      return false;
    }
  }, [parsedAmount, decimalAdjustedMaxWithdrawable]);

  const isBelowFees = !!parsedAmount && feeRaw !== undefined && parsedAmount <= feeRaw;
  const quoteReady = !!acrossQuoteData?.quote;
  const bridgeDelivered = bridgeTxHash && destinationArrivalState.status === 'delivered';
  const isPartialArrival = bridgeTxHash && destinationArrivalState.status === 'partial_arrival';
  const isArrivalTimeout = bridgeTxHash && destinationArrivalState.status === 'arrival_timeout';
  const destinationBaselineReady = preDestinationBalance !== undefined;

  const buttonState: NadoWithdrawButtonState = useMemo(() => {
    if (!parsedAmount || parsedAmount === BigInt("0")) return 'idle';
    if (!route || isAcrossQuoteError) return 'route_unavailable';
    if (hasWalletMismatch) return 'wallet_mismatch';
    if (isBelowFees) return 'idle';
    if (feeRaw === undefined || !expectedBridgeInputAmount || !lockedAddress) return 'idle';

    if (withdrawComplete) {
      if (bridgeDelivered) return 'success';
      if (isPartialArrival) return 'partial_arrival';
      if (isArrivalTimeout) return 'arrival_timeout';
      if (bridgeTxHash) {
        if (acrossExecute.isPending) return 'bridging';
        return 'bridge_pending';
      }
      if (!quoteReady) return isAcrossQuoteLoading ? 'idle' : 'route_unavailable';
      if (!destinationBaselineReady) return 'preparing_bridge';
      if (acrossExecute.isPending) return 'bridging';
      return 'ready_bridge';
    }

    if (withdrawSubmitted || nadoWithdraw.isPending) return 'withdrawing';
    if (!hasEnoughBalance) return 'idle';
    if (!quoteReady) return isAcrossQuoteLoading ? 'idle' : 'route_unavailable';
    if (!isOnInk) return isSwitchingChain ? 'switching_chain' : 'requires_switch_chain';
    return 'ready_withdraw';
  }, [
    acrossExecute.isPending,
    bridgeDelivered,
    bridgeTxHash,
    destinationBaselineReady,
    expectedBridgeInputAmount,
    feeRaw,
    hasEnoughBalance,
    hasWalletMismatch,
    isArrivalTimeout,
    isAcrossQuoteError,
    isAcrossQuoteLoading,
    isBelowFees,
    isOnInk,
    isPartialArrival,
    isSwitchingChain,
    lockedAddress,
    nadoWithdraw.isPending,
    parsedAmount,
    quoteReady,
    route,
    withdrawComplete,
    withdrawSubmitted,
  ]);

  function clearError() {
    setErrorMessage(undefined);
    setErrorDetails(undefined);
  }

  function setError(err: unknown) {
    const details = err instanceof Error ? err.message : String(err);
    setErrorDetails(details);

    if (details.includes('Wallet not connected')) {
      setErrorMessage('Please refresh the page or reconnect your wallet');
    } else if (details.includes('insufficient funds for transfer')) {
      setErrorMessage('Insufficient funds for transfer + gas tx');
    } else if (details.toLowerCase().includes('user rejected') || details.toLowerCase().includes('user denied')) {
      setErrorMessage('Transaction rejected by wallet');
    } else if (details.includes('nonce')) {
      setErrorMessage('Transaction nonce error — try again');
    } else if (details.toLowerCase().includes('timeout')) {
      setErrorMessage('Request timed out — check your connection');
    } else {
      setErrorMessage('Transaction failed — see details');
    }
  }

  const stopFundingAutomationRun = useCallback(() => {
    lastAutomationSubmittedStateRef.current = undefined;
    setIsFundingAutomationActive(false);
  }, []);

  const resetFormSession = useCallback(() => {
    formSessionVersionRef.current += 1;
    stopFundingAutomationRun();
  }, [stopFundingAutomationRun]);

  const resetSession = useCallback(() => {
    resetFormSession();
    setBridgeTxHash(undefined);
    setWithdrawSubmitted(false);
    setWithdrawComplete(false);
    setPreWithdrawBalance(undefined);
    setPreDestinationBalance(undefined);
    setLockedAddress(undefined);
    setBridgeSubmittedAtMs(undefined);
    setExpectedDestinationOutputAmount(undefined);
    setArrivalNowMs(Date.now());
    fillRefreshRequestedRef.current = false;
    clearError();
  }, [resetFormSession]);

  const onAmountChange = useCallback((newAmount: string) => {
    setAmount(newAmount);
    resetSession();
  }, [resetSession]);

  const onFractionSelected = useCallback(
    (fraction: number) => {
      if (!decimalAdjustedMaxWithdrawable) return;
      try {
        const maxRaw = BigInt(
          decimalAdjustedMaxWithdrawable
            .multipliedBy(RAW_TOKEN_DECIMALS)
            .integerValue()
            .toString(),
        );
        const fractionAmount = (maxRaw * BigInt(Math.round(fraction * 10000))) / BigInt("10000");
        setAmount((Number(fractionAmount) / RAW_TOKEN_DECIMALS).toFixed(6));
        resetSession();
      } catch {
        // Ignore formatting errors.
      }
    },
    [decimalAdjustedMaxWithdrawable, resetSession],
  );

  const onBridgeModeChange = useCallback(
    (newBridgeMode: BridgeMode) => {
      resetFormSession();
      setBridgeMode(newBridgeMode);
    },
    [resetFormSession],
  );

  const onSubmit = useCallback(async () => {
    if (!parsedAmount || !address) return;
    clearError();
    const submitSessionVersion = formSessionVersionRef.current;
    const isCurrentSubmitSession = () => formSessionVersionRef.current === submitSessionVersion;

    try {
      switch (buttonState) {
        case 'requires_switch_chain': {
          await switchChainAsync({ chainId: INK_DEPOSIT_CHAIN.chainId });
          break;
        }

        case 'ready_withdraw': {
          if (!route || !lockedAddress || !expectedBridgeInputAmount || feeRaw === undefined || !quoteReady) {
            setErrorMessage('Bridge quote is not ready yet');
            return;
          }
          setPreWithdrawBalance(inkWalletBalance?.raw);
          if (preDestinationBalance === undefined) {
            setPreDestinationBalance(destinationWalletBalance?.raw);
          }
          await nadoWithdraw.mutateAsync({
            productId: route.nadoProductId,
            amount: expectedBridgeInputAmount.toString(),
            spotLeverage: false,
          });
          if (!isCurrentSubmitSession()) return;
          setWithdrawSubmitted(true);
          break;
        }

        case 'ready_bridge': {
          if (!route || !acrossQuoteData?.quote || preDestinationBalance === undefined) {
            setErrorMessage('Bridge is not ready yet');
            return;
          }
          setExpectedDestinationOutputAmount(acrossQuoteData.outputAmount);
          const result = await acrossExecute.mutateAsync({
            quote: acrossQuoteData.quote,
            onProgress: (step, status) => {
              if (!isCurrentSubmitSession()) return;
              if (step === 'fill' && status === 'success') {
                fillRefreshRequestedRef.current = true;
                queryClient.invalidateQueries({
                  queryKey: [
                    'sourceTokenBalance',
                    route.destinationChainId,
                    lockedAddress,
                    route.destinationOutputToken,
                  ],
                });
              }
            },
          });
          if (!isCurrentSubmitSession()) return;
          if (result?.sourceTxHash) {
            setBridgeTxHash(result.sourceTxHash);
            setBridgeSubmittedAtMs((current) => current ?? Date.now());
          }
          if (result?.fillConfirmed || result?.fillTxHash) {
            fillRefreshRequestedRef.current = true;
            queryClient.invalidateQueries({
              queryKey: [
                'sourceTokenBalance',
                route.destinationChainId,
                lockedAddress,
                route.destinationOutputToken,
              ],
            });
          }
          break;
        }

        case 'partial_arrival':
        case 'arrival_timeout': {
          setArrivalNowMs(Date.now());
          queryClient.invalidateQueries({
            queryKey: [
              'sourceTokenBalance',
              route?.destinationChainId,
              lockedAddress,
              route?.destinationOutputToken,
            ],
          });
          break;
        }

        default:
          break;
      }
    } catch (err) {
      if (!isCurrentSubmitSession()) return;
      console.error('[NadoWithdraw] Action failed:', err);
      setIsFundingAutomationActive(false);
      setError(err);
    }
  }, [
    acrossExecute,
    acrossQuoteData?.quote,
    address,
    buttonState,
    destinationWalletBalance?.raw,
    expectedBridgeInputAmount,
    feeRaw,
    inkWalletBalance?.raw,
    lockedAddress,
    nadoWithdraw,
    parsedAmount,
    preDestinationBalance,
    queryClient,
    quoteReady,
    route,
    switchChainAsync,
  ]);

  const automationStatus = getNadoFundingAutomationStatus({
    flow: 'withdraw',
    buttonState,
    featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
    isPrivyEmbeddedWallet,
    isActive: isFundingAutomationActive,
  });

  const runAutomationStep = useCallback(() => {
    if (automationSubmitInFlightRef.current) {
      return;
    }

    if (!shouldSubmitNadoFundingAutomationStep({
      flow: 'withdraw',
      buttonState,
      featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
      isPrivyEmbeddedWallet,
      isActive: isFundingAutomationActive,
      errorMessage,
      lastSubmittedButtonState: lastAutomationSubmittedStateRef.current,
    })) {
      return;
    }

    lastAutomationSubmittedStateRef.current = buttonState;
    automationSubmitInFlightRef.current = true;
    Promise.resolve(onSubmit()).finally(() => {
      automationSubmitInFlightRef.current = false;
    });
  }, [buttonState, errorMessage, isFundingAutomationActive, isPrivyEmbeddedWallet, onSubmit]);

  const onStartFundingAutomation = useCallback(() => {
    if (automationSubmitInFlightRef.current) {
      return;
    }

    lastAutomationSubmittedStateRef.current = undefined;

    if (
      automationStatus.isAvailable &&
      !canStartNadoFundingAutomationStep({
        flow: 'withdraw',
        buttonState,
        featureEnabled: clientEnv.integrations.isPrivyFundingAutomationEnabled,
        isPrivyEmbeddedWallet,
        isActive: true,
      })
    ) {
      return;
    }

    automationSubmitInFlightRef.current = true;
    clearError();

    if (!automationStatus.isAvailable) {
      Promise.resolve(onSubmit()).finally(() => {
        automationSubmitInFlightRef.current = false;
      });
      return;
    }

    setIsFundingAutomationActive(true);
    lastAutomationSubmittedStateRef.current = buttonState;
    Promise.resolve(onSubmit()).finally(() => {
      automationSubmitInFlightRef.current = false;
    });
  }, [automationStatus.isAvailable, buttonState, isPrivyEmbeddedWallet, onSubmit]);

  const onStopFundingAutomation = useCallback(() => {
    stopFundingAutomationRun();
  }, [stopFundingAutomationRun]);

  useEffect(() => {
    if (!automationStatus.shouldAutoSubmit) {
      return;
    }

    runAutomationStep();
  }, [automationStatus.shouldAutoSubmit, buttonState, runAutomationStep]);

  useEffect(() => {
    if (!isFundingAutomationActive) {
      return;
    }

    if (
      shouldStopNadoFundingAutomation({
        flow: 'withdraw',
        buttonState,
        errorMessage,
      })
    ) {
      setIsFundingAutomationActive(false);
    }
  }, [buttonState, errorMessage, isFundingAutomationActive]);

  const lzFeeDisplay = useMemo(() => {
    if (!route) return '0';
    if (!acrossQuoteData) return '~0';
    return `~$${formatTokenAmount(acrossQuoteData.relayFeeTotal, 4)} ${route.inkSourceSymbol}`;
  }, [acrossQuoteData, route]);

  const acrossFeeFormatted = route && acrossQuoteData
    ? `~$${formatTokenAmount(acrossQuoteData.relayFeeTotal, 4)} ${route.inkSourceSymbol}`
    : undefined;

  const routeErrorDetails = acrossQuoteError instanceof Error
    ? acrossQuoteError.message
    : acrossQuoteError
      ? String(acrossQuoteError)
      : undefined;
  const routeErrorMessage =
    buttonState === 'route_unavailable'
      ? 'Ethereum bridge route is not available right now'
      : buttonState === 'wallet_mismatch'
        ? 'Wallet changed during withdrawal — reset and try again'
        : undefined;

  const formData: NadoWithdrawFormData = {
    selectedChainId,
    selectedTokenSymbol,
    destinationTokenSymbol,
    amount,
    availableBalance: availableBalanceFormatted,
    estimatedReceiveAmount: acrossQuoteData
      ? formatTokenAmount(acrossQuoteData.outputAmount, 4)
      : formatTokenAmount(expectedBridgeInputAmount, 4),
    lzFee: lzFeeDisplay,
    withdrawalFee: withdrawalFee ? withdrawalFee.toFixed(6) : undefined,
    isOnInk,
    buttonState: isBelowFees ? 'idle' : buttonState,
    bridgeProvider: route ? 'across' : 'none',
    bridgeMode,
    showBridgeModeSelector: false,
    acrossFee: acrossFeeFormatted,
    isAcrossQuoteLoading,
    bridgeTxHash,
    inputError: isBelowFees ? 'below_fees' : undefined,
    errorMessage: errorMessage ?? routeErrorMessage,
    errorDetails: errorDetails ?? routeErrorDetails,
    isFundingAutomationAvailable: automationStatus.isAvailable,
    isFundingAutomationActive: automationStatus.isActive,
  };

  const formActions: NadoWithdrawFormActions = {
    onAmountChange,
    onFractionSelected,
    onBridgeModeChange,
    onSubmit,
    onStartFundingAutomation,
    onStopFundingAutomation,
  };

  const bridgeProgressStatus: NadoWithdrawBridgeProgressData['status'] =
    bridgeDelivered
      ? 'delivered'
      : isPartialArrival
        ? 'partial_arrival'
        : isArrivalTimeout
          ? 'arrival_timeout'
          : 'in_transit';

  const bridgeProgress: NadoWithdrawBridgeProgressData | undefined =
    route && (bridgeTxHash || bridgeDelivered) && destChainConfig
      ? {
          status: bridgeProgressStatus,
          sourceTxHash: bridgeTxHash,
          destinationChain: destChainConfig,
          tokenSymbol: destinationTokenSymbol,
        }
      : undefined;

  return { formData, formActions, bridgeProgress };
}
